// hooks/useCandidateSearch.js
/**
 * useCandidateSearch — candidate search and selection logic for the accordion layout.
 *
 * Manages:
 *  - Employee list (all active employees for manual search)
 *  - handleFindCandidates — full IncrementalTeamScorer v3 flow, writes to per-role pool
 *  - handleSelectSearchCandidate — load and display a searched candidate tile
 *  - handleAnalyzeChemistry — on-demand chemistry scoring, updates per-role pool
 *  - fetchCandidatePerformanceData — /full endpoint wrapper with memoization
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Logger } from '../../../utils/logger';
import { apiFetch, apiGet } from '../../../utils/api';

const logger = new Logger('useCandidateSearch');

/**
 * @param {object} params
 * @param {object|null}   params.staffingData
 * @param {object}        params.roleCandidatePools          — from staffingStateManager
 * @param {function}      params.setRoleCandidates           — from staffingStateManager
 * @param {function}      params.setRoleSearching            — from staffingStateManager
 * @param {function}      params.setRoleSearchCandidate      — from staffingStateManager
 * @param {object}        params.candidatePerformanceData    — from staffingStateManager
 * @param {function}      params.setCandidatePerformanceData — from staffingStateManager
 */
export function useCandidateSearch({
  staffingData,
  roleCandidatePools,
  setRoleCandidates,
  setRoleSearching,
  setRoleSearchCandidate,
  candidatePerformanceData,
  setCandidatePerformanceData,
  getEffectiveStrategy,
}) {
  // ── Local state ───────────────────────────────────────────────────────────
  const [allEmployees, setAllEmployees]                 = useState([]);
  const [allTechFitOpen, setAllTechFitOpen]             = useState(false);
  const [loadingPerformanceData, setLoadingPerformanceData]   = useState({});

  // ── Refs ──────────────────────────────────────────────────────────────────
  // Tracks all employee IDs already shown per role so "get more" never repeats them
  const shownCandidateIdsRef     = useRef({});

  // ── Helpers ───────────────────────────────────────────────────────────────

  const getExistingTeamMembers = useCallback(() => {
    if (!staffingData) return [];
    return staffingData.roles
      .filter(r => r.status === 'assigned' && r.assigned_employee_id)
      .map(r => ({
        employee_id: r.assigned_employee_id,
        employee_name: r.assigned_employee_name,
        disc_type: r.assigned_disc_type || 'S',
        top_skills: r.assigned_skills || [],
      }));
  }, [staffingData]);

  const fetchCandidatePerformanceData = useCallback(async (employeeId) => {
    if (candidatePerformanceData[employeeId] || loadingPerformanceData[employeeId]) {
      return candidatePerformanceData[employeeId];
    }

    setLoadingPerformanceData(prev => ({ ...prev, [employeeId]: true }));
    try {
      const [fullData, compensationData] = await Promise.all([
        apiGet(`employees/${employeeId}/full`),
        apiGet(`employees/${employeeId}/compensation`).catch(() => ({})),
      ]);

      const performance  = fullData.performance  || {};
      const discProfile  = fullData.disc_profile || {};

      const calculateDISC = (disc) => {
        if (!disc || (!disc.dominance && !disc.influence && !disc.steadiness && !disc.conscientiousness)) return 'N/A';
        const scores = [
          { letter: 'D', value: disc.dominance        || 0 },
          { letter: 'I', value: disc.influence         || 0 },
          { letter: 'S', value: disc.steadiness        || 0 },
          { letter: 'C', value: disc.conscientiousness || 0 },
        ];
        scores.sort((a, b) => b.value - a.value);
        const top2 = scores.slice(0, 2).filter(s => s.value >= 5);
        return top2.length > 0 ? top2.map(s => s.letter).join('') : 'N/A';
      };

      const getCommunicationScore = (data) => {
        const softSkills = data.soft_skills || [];
        const commSkill = softSkills.find(s =>
          s.category?.toLowerCase() === 'communication' ||
          s.name?.toLowerCase().includes('communication')
        );
        return commSkill?.score || null;
      };

      const discProfileDisplay = fullData.disc_profile_display || calculateDISC(discProfile);
      const csotValue = performance.csot_12_weeks || null;

      const costRateRaw = compensationData.hourly_rate ||
        compensationData.totalCompensation?.hourlyCost ||
        compensationData.hourly_cost || null;

      // Resolve next_available_date from multiple possible locations in the response
      const nextAvailDate =
        fullData.availability?.next_available_date ||
        fullData.next_available_date ||
        fullData.available_date ||
        null;

      // Current active project name from engagement data
      const currentProject =
        fullData.engagement?.current_engagements?.[0]?.project_name || null;

      const perf = {
        dir_score: performance.dir_complete || performance.dir_score || null,
        csot_12_weeks: csotValue,
        csot: csotValue,
        csat: performance.avgCSAT || performance.csat || null,
        utilization_13_weeks: performance.utilization_13_weeks || null,
        overall_technical_score: fullData.overall_technical_score || null,
        disc_profile_display: discProfileDisplay,
        disc_profile: discProfileDisplay,
        home_location: fullData.home_location || fullData.location || 'N/A',
        communication_score: getCommunicationScore(fullData),
        strengths: fullData.strengths || [],
        hire_date: fullData.hire_date || null,
        years_experience: fullData.years_experience || null,
        professional_skills: fullData.professional_skills || null,
        availability: fullData.availability || { status: 'AVAILABLE' },
        next_available_date: nextAvailDate,
        current_project: currentProject,
        csot_4_weeks: performance.csot_4_weeks || null,
        csot_8_weeks: performance.csot_8_weeks || null,
        cost_rate: costRateRaw,
      };

      setCandidatePerformanceData(prev => ({ ...prev, [employeeId]: perf }));
      return perf;
    } catch (error) {
      logger.error('Error fetching performance data for', employeeId, error);
      return null;
    } finally {
      setLoadingPerformanceData(prev => ({ ...prev, [employeeId]: false }));
    }
  }, [candidatePerformanceData, loadingPerformanceData, setCandidatePerformanceData]);

  // ── handleFindCandidates ──────────────────────────────────────────────────

  const handleFindCandidates = useCallback(async (role, forceRefresh = false) => {
    const roleId = role.id || role.role_name;

    setRoleSearching(roleId, true);

    try {
      const existingTeam = getExistingTeamMembers();

      let isoStartDate = null;
      const dateToUse = role.role_start_date || staffingData?.start_date;
      if (dateToUse) {
        try {
          const date = new Date(dateToUse);
          if (!isNaN(date.getTime())) isoStartDate = date.toISOString().split('T')[0];
        } catch (e) { logger.warn('Could not parse start_date:', dateToUse); }
      }

      let isoEndDate = null;
      const endDateToUse = role.role_end_date || role.end_date;
      if (endDateToUse) {
        try { isoEndDate = new Date(endDateToUse).toISOString().split('T')[0]; } catch (e) { /* ignore */ }
      } else if (isoStartDate && role.role_duration_weeks) {
        const startDt = new Date(isoStartDate);
        startDt.setDate(startDt.getDate() + role.role_duration_weeks * 7);
        isoEndDate = startDt.toISOString().split('T')[0];
      }

      const shownIds = forceRefresh ? [] : [...(shownCandidateIdsRef.current[roleId] || new Set())];
      const currentlyAssignedIds = (staffingData?.roles || [])
        .filter(r => r.status === 'assigned' && r.assigned_employee_id)
        .map(r => r.assigned_employee_id);
      const excludeIds = [...new Set([...shownIds, ...currentlyAssignedIds])];

      const requestData = {
        opportunity_id: staffingData?.opportunity_id || staffingData?.originalData?.opportunity_id || staffingData?.id,
        role_name: role.role_name,
        existing_team: existingTeam,
        start_date: isoStartDate,
        end_date: isoEndDate,
        required_skills: role.skills || [],
        exclude_ids: excludeIds,
        strategy: getEffectiveStrategy ? getEffectiveStrategy(role) : 'balanced',
      };

      const response = await apiFetch('staffing/suggest-candidates-v3', {
        method: 'POST',
        body: JSON.stringify(requestData),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      const candidates = Array.isArray(data.top_candidates) ? data.top_candidates : [];

      const commentary = `Concierge is suggesting these ${candidates.length} candidate${candidates.length !== 1 ? 's' : ''} for ${role.role_name} from ${data.total_qualified || 0} qualified.${existingTeam.length > 0 ? ` Team chemistry with ${existingTeam.length} existing member${existingTeam.length !== 1 ? 's' : ''} factored in.` : ''}`;

      const mappedCandidates = await Promise.all(candidates.map(async candidate => {
        const fullName   = candidate.employee_name || '';
        const nameParts  = fullName.split(' ');
        const firstName  = candidate.first_name || nameParts[0] || 'Unknown';
        const lastName   = candidate.last_name  || nameParts.slice(1).join(' ') || '';

        let totalExperience = 0;
        if (candidate.professional_skills) {
          Object.values(candidate.professional_skills).forEach(cat => {
            if (Array.isArray(cat)) cat.forEach(s => {
              if (s.years_experience && s.years_experience > totalExperience) totalExperience = s.years_experience;
            });
          });
        }

        const extractedSkills = [];
        if (candidate.professional_skills) {
          const allSkills = [];
          Object.entries(candidate.professional_skills).forEach(([, skills]) => {
            if (Array.isArray(skills)) skills.forEach(s => { if (s.name) allSkills.push({ name: s.name, proficiency_score: s.proficiency_score || 0, proficiency_level: s.proficiency_level }); });
          });
          const order = { 'Expert': 3, 'Advanced': 2, 'Intermediate': 1, 'Beginner': 0 };
          allSkills.sort((a, b) => {
            const aL = order[a.proficiency_level] || 0, bL = order[b.proficiency_level] || 0;
            return aL !== bL ? bL - aL : (b.proficiency_score || 0) - (a.proficiency_score || 0);
          });
          allSkills.forEach(s => { if (extractedSkills.length < 8) extractedSkills.push(s.name); });
        }
        if (extractedSkills.length === 0 && candidate.top_technical_categories) {
          candidate.top_technical_categories.forEach(c => { if (extractedSkills.length < 5) extractedSkills.push(c); });
        }
        if (extractedSkills.length === 0 && candidate.skills_summary?.primary_expertise) {
          candidate.skills_summary.primary_expertise.forEach(s => { if (extractedSkills.length < 5) extractedSkills.push(s); });
        }

        let performanceData = null;
        try {
          performanceData = await fetchCandidatePerformanceData(candidate.employee_id);
        } catch (error) {
          performanceData = {
            dir_score: candidate.dir_complete || candidate.performance_metrics?.dir_complete || null,
            csot_12_weeks: candidate.csot || candidate.performance_metrics?.csot_12_weeks || null,
            csat: candidate.csat || candidate.performance_metrics?.csat || null,
            utilization_13_weeks: candidate.utilization || candidate.performance_metrics?.utilization_13_weeks || null,
            disc_profile_display: candidate.disc_profile || candidate.personality?.disc_profile || null,
            home_location: candidate.location || null,
            communication_score: candidate.communication_score || null,
            hourly_rate: candidate.hourly_rate || null,
            billing_rate: candidate.billing_rate || null,
            hire_date: candidate.hire_date || null,
          };
        }

        let experienceYears = totalExperience;
        if (experienceYears === 0 && (performanceData?.hire_date || candidate.hire_date)) {
          const hireDate    = new Date(performanceData?.hire_date || candidate.hire_date);
          const currentDate = new Date();
          const yearsDiff   = currentDate.getFullYear() - hireDate.getFullYear();
          const monthDiff   = currentDate.getMonth() - hireDate.getMonth();
          experienceYears   = Math.max(yearsDiff + (monthDiff >= 0 ? 0 : -1), 1);
        }

        const fmtNum  = v => (v === null || v === undefined || isNaN(v)) ? 'N/A' : typeof v === 'number' ? v.toFixed(1) : String(v);
        const fmtCur  = v => (v === null || v === undefined || isNaN(v)) ? 'N/A' : `$${Number(v).toFixed(2)}`;
        const fmtPct  = v => (v === null || v === undefined || isNaN(v)) ? 'N/A' : `${Number(v).toFixed(1)}%`;

        return {
          employee_id: candidate.employee_id,
          first_name: firstName, last_name: lastName,
          title: candidate.title || 'N/A', department: candidate.department || 'N/A',
          years_experience: experienceYears > 0 ? experienceYears : 'N/A',
          availability: candidate.availability || { status: 'AVAILABLE' },
          overall_technical_score: candidate.overall_technical_score,
          availability_reason: candidate.availability_reason,
          availability_tier: candidate.tier,
          next_available_date: candidate.next_available_date,
          is_recommended: candidate.is_recommended || false,
          overall_rank: candidate.overall_rank || null,
          composite_score: candidate.composite_score || null,
          recommendation_reasons: candidate.recommendation_reasons || [],
          match_score: candidate.composite_score || null,
          why_recommended: candidate.recommendation_reasons || [],
          dir_score: performanceData?.dir_score != null ? fmtNum(performanceData.dir_score) : 'N/A',
          csot: performanceData?.csot_12_weeks != null ? fmtPct(performanceData.csot_12_weeks) : 'N/A',
          csot_4_week: candidate.csot_4_week,
          csot_8_week: candidate.csot_8_week,
          csot_12_weeks: candidate.csot_12_weeks,
          csat: performanceData?.csat != null ? fmtNum(performanceData.csat) : 'N/A',
          utilization: performanceData?.utilization_13_weeks != null ? fmtPct(performanceData.utilization_13_weeks) : 'N/A',
          disc_profile: performanceData?.disc_profile_display || candidate.disc_profile || 'N/A',
          strengths: performanceData?.strengths || [],
          communication_score: performanceData?.communication_score ?? 'N/A',
          location: performanceData?.home_location || candidate.location || 'N/A',
          cost_rate: performanceData?.cost_rate ?? candidate.cost_rate ?? null,
          hourly_rate: fmtCur(performanceData?.hourly_rate || performanceData?.billing_rate || candidate.hourly_rate),
          billing_rate: fmtCur(performanceData?.billing_rate || performanceData?.hourly_rate || candidate.billing_rate),
          top_skills: extractedSkills.slice(0, 5),
          skills: extractedSkills,
          availability_label: candidate.availability_label || candidate.availability_reason || 'Available',
          risk_level: candidate.risk_level || 'green',
          availability_detail: candidate.availability_detail || '',
          selection_reasons: candidate.selection_reasons || [],
          selection_prose: candidate.selection_prose || '',
          availability_facts: candidate.availability_facts || {},
          skills_breakdown: candidate.skills_breakdown || [],
          skills_breadth_percentage: candidate.skills_breadth_percentage || candidate.coverage_percentage,
          skills_breadth_count: candidate.skills_breadth_count || candidate.matched_skills_count,
          skills_breadth_total: candidate.skills_breadth_total || candidate.total_required_skills,
          skills_depth_average: candidate.skills_depth_average,
        };
      }));

      // Deduplicate
      const seenIds = new Set();
      const deduped = mappedCandidates.filter(c => {
        if (seenIds.has(c.employee_id)) return false;
        seenIds.add(c.employee_id);
        return true;
      });

      // Filter out already-assigned employees
      const assignedIds = new Set(
        (staffingData?.roles || [])
          .filter(r => r.status === 'assigned' && r.assigned_employee_id)
          .map(r => r.assigned_employee_id)
      );
      const filtered = deduped.filter(c => !assignedIds.has(c.employee_id));

      setRoleCandidates(roleId, filtered, commentary);

      // Accumulate shown IDs so "get more" never repeats them
      const existing = shownCandidateIdsRef.current[roleId] || new Set();
      filtered.forEach(c => existing.add(c.employee_id));
      shownCandidateIdsRef.current[roleId] = existing;

      // Save search session (non-critical)
      try {
        const sessionPayload = {
          project_id: staffingData?.id,
          configuration_slot: 1,
          role_name: role.role_name,
          concierge_analysis: data.response || '',
          candidate_results: mappedCandidates,
          search_parameters: { role_level: role.level, required_experience: role.required_experience_years, skills: role.skills, search_timestamp: new Date().toISOString() },
          role_requirements: { role_name: role.role_name, level: role.level, skills: role.skills, required_experience_years: role.required_experience_years, total_hours: role.total_hours, hourly_rate: role.hourly_rate },
          search_context: { project_name: staffingData?.projectName, client_name: staffingData?.clientName, urgency: staffingData?.priority, team_size: staffingData?.roles?.length },
        };
        const sr = await apiFetch('team-optimization/search-sessions', { method: 'POST', body: JSON.stringify(sessionPayload) });
        if (sr.ok) { const res = await sr.json(); logger.debug('✅ Search session saved:', res.search_session_id); }
      } catch (e) { logger.debug('⚠️ Error saving search session (non-critical):', e); }

    } catch (error) {
      logger.error('Error finding candidates:', error);
      setRoleCandidates(roleId, [], '');
    } finally {
      setRoleSearching(roleId, false);
    }
  }, [
    staffingData,
    setRoleCandidates, setRoleSearching,
    getExistingTeamMembers, fetchCandidatePerformanceData,
    getEffectiveStrategy,
  ]);

  // ── handleSelectSearchCandidate ───────────────────────────────────────────

  const handleSelectSearchCandidate = useCallback(async (employee, role) => {
    if (!employee || !role) return;
    const roleId = role.id || role.role_name;

    // Show loading placeholder immediately
    setRoleSearchCandidate(roleId, { ...employee, narrativeLoading: true });

    try {
      const performanceData = await fetchCandidatePerformanceData(employee.employee_id);

      const searchTopSkills = [];
      const profSkills = performanceData?.professional_skills || employee?.professional_skills;
      if (profSkills) {
        const allSkills = [];
        Object.values(profSkills).forEach(cat => {
          if (Array.isArray(cat)) cat.forEach(s => { if (s.name) allSkills.push({ name: s.name, level: s.proficiency_level || 0 }); });
        });
        allSkills.sort((a, b) => b.level - a.level).slice(0, 5).forEach(s => searchTopSkills.push(s.name));
      }

      // Fetch full availability facts from dedicated endpoint (includes soft bookings,
      // current project from assignments collection, and correct overlap_days)
      let availFacts = null;
      try {
        const afParams = new URLSearchParams({ employee_id: employee.employee_id });
        if (role?.role_start_date) afParams.set('role_start_date', role.role_start_date);
        availFacts = await apiGet(`employees/${employee.employee_id}/availability-facts?${afParams}`);
      } catch(e) {
        logger.debug('availability-facts fetch failed, falling back to perf data:', e.message);
      }

      // Build availability display values — prefer dedicated endpoint, fall back to /full data
      const searchAvailStatus = availFacts?.availability_status
        || performanceData?.availability?.status
        || 'AVAILABLE';
      const engEndFormatted = availFacts?.current_engagement_ends
        || performanceData?.next_available_date
        || performanceData?.availability?.next_available_date
        || null;
      const overlapDays    = availFacts?.overlap_days ?? 0;
      const currentProject = availFacts?.current_project || performanceData?.current_project || null;
      const softBookings   = availFacts?.soft_bookings || [];

      const isAvailableNow = searchAvailStatus === 'AVAILABLE' || !engEndFormatted;

      const searchRiskLevel = isAvailableNow ? 'green'
        : overlapDays > 14 ? 'orange' : 'yellow';

      const searchAvailLabel = isAvailableNow
        ? 'Available Now'
        : engEndFormatted
          ? (overlapDays > 0
              ? `${overlapDays}d overlap`
              : `Free ${Math.abs(overlapDays)}d before start`)
          : 'Check availability';

      const searchAvailDetail = isAvailableNow
        ? 'Currently available for new projects'
        : engEndFormatted
          ? (overlapDays > 0
              ? `${currentProject ? `On ${currentProject} until ` : 'Engagement ends '}${engEndFormatted} — ${overlapDays}d overlap with role start`
              : `${currentProject ? `On ${currentProject}, finishing ` : 'Engagement ends '}${engEndFormatted} — free ${Math.abs(overlapDays)}d before role start`)
          : '';

      const searchAvail = performanceData?.availability || { status: searchAvailStatus };

      let candidateWithData = {
        ...employee, ...performanceData,
        overall_technical_score: performanceData?.overall_technical_score || null,
        composite_score: null,
        dir_score: performanceData?.dir_score || null,
        csat: performanceData?.csat || null, csot: performanceData?.csot || null,
        disc_profile: performanceData?.disc_profile_display || performanceData?.disc_profile || null,
        communication_score: performanceData?.communication_score || null,
        years_experience: performanceData?.years_experience || null,
        location: performanceData?.home_location || employee.location || null,
        availability: searchAvail,
        cost_rate: performanceData?.cost_rate ?? null,
        hourly_rate: performanceData?.hourly_rate || performanceData?.billing_rate || null,
        top_skills: searchTopSkills,
        risk_level: searchRiskLevel,
        availability_label: searchAvailLabel,
        availability_detail: searchAvailDetail,
        selection_reasons: [], selection_prose: '', narrativeLoading: true,
        availability_facts: {
          csot_4_weeks: availFacts?.csot_4_weeks ?? performanceData?.csot_4_weeks ?? null,
          csot_8_weeks: availFacts?.csot_8_weeks ?? performanceData?.csot_8_weeks ?? null,
          current_engagement_ends: engEndFormatted,
          overlap_days: overlapDays,
          current_project: currentProject,
          soft_bookings: softBookings,
        },
      };

      setRoleSearchCandidate(roleId, candidateWithData);

      // Try to get a role-specific tech score
      if (staffingData) {
        const existingPool = roleCandidatePools[roleId]?.candidates || [];
        const existingCandidate = existingPool.find(c => c.employee_id === employee.employee_id);
        if (existingCandidate && existingCandidate.overall_technical_score) {
          candidateWithData = {
            ...candidateWithData,
            overall_technical_score: existingCandidate.overall_technical_score,
            composite_score: existingCandidate.composite_score,
            skills_breakdown: existingCandidate.skills_breakdown || [],
            skills_breadth_percentage: existingCandidate.skills_breadth_percentage,
            skills_breadth_count: existingCandidate.skills_breadth_count,
            skills_breadth_total: existingCandidate.skills_breadth_total,
            skills_depth_average: existingCandidate.skills_depth_average,
          };
          setRoleSearchCandidate(roleId, candidateWithData);
        } else {
          try {
            const params = new URLSearchParams({
              employee_id: employee.employee_id,
              opportunity_id: staffingData.opportunity_id || staffingData.id,
              role_name: role.role_name,
            });
            const scoreData = await apiGet(`staffing/get-employee-role-score?${params}`);
            if (scoreData.success) {
              candidateWithData = {
                ...candidateWithData,
                overall_technical_score: scoreData.overall_technical_score,
                coverage_percentage: scoreData.coverage_percentage,
                matched_skills_count: scoreData.matched_skills_count,
                total_required_skills: scoreData.total_required_skills,
                skills_breakdown: scoreData.skills_breakdown || [],
                skills_breadth_percentage: scoreData.skills_breadth_percentage,
                skills_breadth_count: scoreData.skills_breadth_count,
                skills_breadth_total: scoreData.skills_breadth_total,
                skills_depth_average: scoreData.skills_depth_average,
              };
              setRoleSearchCandidate(roleId, candidateWithData);
            }
          } catch (error) {
            logger.debug('ℹ️ Error fetching precomputed score:', error.message);
          }
        }

        // Generate narrative
        try {
          const narrativeRes = await apiFetch('staffing/narrate-candidate', {
            method: 'POST',
            body: JSON.stringify({
              candidate: {
                employee_id: employee.employee_id,
                first_name: employee.first_name || '',
                last_name: employee.last_name || '',
                title: candidateWithData.title,
                overall_technical_score: candidateWithData.overall_technical_score,
                top_skills: candidateWithData.top_skills || [],
                disc_profile: candidateWithData.disc_profile,
                dir_score: typeof candidateWithData.dir_score === 'number' ? candidateWithData.dir_score : null,
                csat: typeof candidateWithData.csat === 'number' ? candidateWithData.csat : null,
                years_experience: candidateWithData.years_experience,
                availability_facts: candidateWithData.availability_facts,
                skills_breakdown: candidateWithData.skills_breakdown || [],
              },
              role_name: role.role_name,
              start_date: staffingData.start_date || null,
              end_date: null,
              required_skills: role.skills || [],
              existing_team: getExistingTeamMembers(),
            }),
          });
          if (narrativeRes.ok) {
            const nd = await narrativeRes.json();
            setRoleSearchCandidate(roleId, {
              ...candidateWithData,
              selection_prose: nd.status === 'success' ? (nd.selection_prose || '') : '',
              selection_reasons: nd.status === 'success' ? (nd.selection_reasons || []) : [],
              narrativeLoading: false,
            });
          } else {
            setRoleSearchCandidate(roleId, { ...candidateWithData, narrativeLoading: false });
          }
        } catch (e) {
          logger.debug('Narrative generation failed (non-critical):', e);
          setRoleSearchCandidate(roleId, { ...candidateWithData, narrativeLoading: false });
        }
      } else {
        setRoleSearchCandidate(roleId, { ...candidateWithData, narrativeLoading: false });
      }
    } catch (error) {
      logger.error('Error loading search candidate:', error);
      setRoleSearchCandidate(roleId, null);
    }
  }, [staffingData, roleCandidatePools, setRoleSearchCandidate, fetchCandidatePerformanceData, getExistingTeamMembers]);

  // ── handleAnalyzeChemistry ────────────────────────────────────────────────

  const handleAnalyzeChemistry = useCallback(async (candidate, role) => {
    if (!role) return;
    const roleId = role.id || role.role_name;
    try {
      const existingTeam = getExistingTeamMembers();
      const requestData  = {
        employee_id: candidate.employee_id,
        opportunity_id: staffingData?.opportunity_id || staffingData?.originalData?.opportunity_id || staffingData?.id,
        role_name: role.role_name,
        existing_team: existingTeam,
      };
      const response = await apiFetch('staffing/analyze-candidate-chemistry', { method: 'POST', body: JSON.stringify(requestData) });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      const currentPool = roleCandidatePools[roleId]?.candidates || [];
      const updatedCandidates = currentPool.map(c =>
        c.employee_id === candidate.employee_id
          ? { ...c, team_fit_score: data.team_fit_score, composite_score: data.composite_score, chemistry_analyzed: true, recommendation_reasons: [...(c.recommendation_reasons || []), ...data.recommendation_reasons] }
          : c
      );
      setRoleCandidates(roleId, updatedCandidates, roleCandidatePools[roleId]?.commentary || '');
    } catch (error) {
      logger.error('Error analyzing chemistry:', error);
    }
  }, [staffingData, roleCandidatePools, setRoleCandidates, getExistingTeamMembers]);

  // ── useEffects ────────────────────────────────────────────────────────────

  // Load all active employees for manual search on mount
  useEffect(() => {
    const loadAllEmployees = async () => {
      try {
        let allEmployeesList = [];
        let page = 1, hasMore = true;
        while (hasMore) {
          const data         = await apiGet(`employees?limit=100&page=${page}`);
          const employeeList = data.employees || [];
          allEmployeesList   = allEmployeesList.concat(employeeList);
          const totalCount   = data.total_count || data.total || 0;
          hasMore            = allEmployeesList.length < totalCount;
          page++;
          if (page > 50) break;
        }
        setAllEmployees(allEmployeesList
          .filter(emp => emp.employee_id && emp.employee_id.startsWith('EMP') && emp.status?.toLowerCase() !== 'terminated')
          .map(emp => ({ employee_id: emp.employee_id, name: `${emp.first_name} ${emp.last_name}`, first_name: emp.first_name, last_name: emp.last_name, title: emp.title }))
        );
      } catch (error) {
        logger.error('🔍 Error loading employees for search:', error);
      }
    };
    loadAllEmployees();
  }, []);

  return {
    // state values
    allEmployees,
    allTechFitOpen, setAllTechFitOpen,
    loadingPerformanceData,
    // handlers
    handleFindCandidates,
    handleSelectSearchCandidate,
    handleAnalyzeChemistry,
    fetchCandidatePerformanceData,
  };
}
